import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { IoMdArrowRoundBack, IoMdClose } from 'react-icons/io';
import { FiEdit2, FiCheck } from 'react-icons/fi';
import { MdOutlineCheckCircle, MdOutlineCancel, MdOutlineArchive, MdOutlinePending, MdOutlineRadioButtonUnchecked } from 'react-icons/md';
import PageForm from '../../templates/PageForm';
import ATable from '../../organisms/ATable';
import Tab, { TabProps } from '../../molecules/Tab';
import SelectTextField, { OptionProps } from '../../molecules/SelectTextField';
import TextEdit from '../../molecules/TextEdit';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import {
    COLLATERAL_BREADCRUMBS,
    COLLATERAL_ROUTES,
    formatCurrency,
    formatDate,
    formatCpfCnpj,
    getGuaranteeStatusColor,
    getIntensityColor,
    getTraderStatusColor,
    getValidationStatusColor,
} from './collateralUtils';

// --- Skeleton ---
const Skeleton = ({ className = '' }: { className?: string }) => (
    <div className={`animate-pulse bg-gray-200 dark:bg-slate-700 rounded ${className}`} />
);

const CardSkeleton = () => (
    <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
        <Skeleton className="h-4 w-24 mb-4" />
        <div className="space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-3 w-full" />)}
        </div>
    </div>
);

const PageSkeleton = () => (
    <div className="p-6 space-y-6">
        <Skeleton className="h-6 w-96 mb-2" />
        <div className="grid grid-cols-3 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
        </div>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full" />
    </div>
);

// --- Field display helpers ---
const Field = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
    <div className="flex justify-between items-center py-0.5">
        <span className="text-gray-500 dark:text-gray-400 text-xs">{label}</span>
        {color ? (
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${color}`}>{value}</span>
        ) : (
            <span className="text-xs dark:text-white text-right max-w-[60%] truncate" title={String(value || '')}>{value || '—'}</span>
        )}
    </div>
);

const SectionTitle = ({ children, color }: { children: string; color?: string }) => (
    <h3 className={`text-[10px] font-semibold uppercase mb-2 tracking-wide ${color || 'text-gray-500 dark:text-gray-400'}`}>{children}</h3>
);

// --- Status translation ---
const STATUS_LABELS: Record<string, string> = {
    NOT_STARTED: 'Não iniciado', IN_PROGRESS: 'Em andamento', CHECKED: 'Conferido',
    DONE: 'Concluído', SETTLED: 'Quitada', PARTIALLY_VALIDATED: 'Parcialmente validada',
    FULLY_VALIDATED: '100% Validada', STARTED: 'Iniciado', IN_VALIDATION: 'Em validação',
    AWAITING_DOC: 'Aguardando doc', HAS_PENDENCY: 'Tem pendência', 'N/A': 'N/A',
    BAIXA: 'BAIXA', MEDIA: 'MÉDIA', ALTA: 'ALTA', CAOTICA: 'CAÓTICA',
};
const t = (v: string) => STATUS_LABELS[v] || v;

// --- Process validation icons ---
const ProcessIcon = ({ received, status }: { received?: boolean; status?: string }) => {
    if (!received) return <MdOutlineRadioButtonUnchecked className="text-gray-300" size={14} title="Não recebido" />;
    switch (status) {
        case 'VALIDATED': return <MdOutlineCheckCircle className="text-green-500" size={14} title="Validado" />;
        case 'REJECTED': return <MdOutlineCancel className="text-red-500" size={14} title="Rejeitado" />;
        case 'ARCHIVED': return <MdOutlineArchive className="text-blue-500" size={14} title="Arquivado" />;
        case 'PENDING': return <MdOutlinePending className="text-amber-500" size={14} title="Pendente" />;
        default: return <MdOutlinePending className="text-amber-500" size={14} title="Pendente" />;
    }
};

// --- Process type labels ---
const PROCESS_LABELS: Record<string, string> = {
    XML: 'XML',
    DRAWEE: 'Sacado',
    INVOICE_PDF: 'NF PDF',
    PURCHASE_CONTRACT: 'Contrato C/V',
    RECEIPT_STUB: 'Canhoto',
    SEFAZ: 'SEFAZ',
    ACCEPTED_BILL: 'Duplicata',
};

// --- All process types (always shown) ---
const ALL_PROCESSES = ['XML', 'DRAWEE', 'INVOICE_PDF', 'PURCHASE_CONTRACT', 'RECEIPT_STUB', 'SEFAZ', 'ACCEPTED_BILL'];
const TAB_ITEMS: TabProps[] = [
    { id: 'general', title: 'Geral', index: 0 },
    { id: 'metrics', title: 'Métricas e checagem', index: 1 },
    { id: 'receivables', title: 'Recebíveis', index: 2 },
];

// --- Main Component ---
const GuaranteePageView = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showValidationPanel, setShowValidationPanel] = useState(false);

    const TAB_KEYS = ['general', 'metrics', 'receivables'];
    const tabIndex = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || 'general'));
    const setTabIndex = (idx: number) => setSearchParams({ tab: TAB_KEYS[idx] });

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getGuaranteeView(id)
            .then(setData)
            .catch((e: any) => {
                if (e instanceof ApiError) {
                    if (e.status === 403) return navigate('/denied');
                    if (e.status === 404) return navigate('/not-found');
                }
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const titles = data?.titles || [];

    // Enabled processes based on validation config
    const enabledProcesses = useMemo(() => {
        if (!data?.validationConfig) return [];
        const config = data.validationConfig;
        const processes: string[] = [];
        if (config.validation_xml) processes.push('XML');
        if (config.validation_contact) processes.push('DRAWEE');
        if (config.validation_invoice_pdf) processes.push('INVOICE_PDF');
        if (config.validation_purchase_contract) processes.push('PURCHASE_CONTRACT');
        if (config.validation_receipt_stub) processes.push('RECEIPT_STUB');
        if (config.validation_sefaz) processes.push('SEFAZ');
        if (config.validation_accepted_bill) processes.push('ACCEPTED_BILL');
        return processes;
    }, [data]);

    const summary = useMemo(() => {
        if (!data) return { totalAmount: 0, validatedAmount: 0, docsCompleteAmount: 0, docsIncompleteAmount: 0 };
        const totalAmount = data.totalAmount || 0;
        const validatedAmount = totalAmount * ((data.validatedPercentage || 0) / 100);
        const docsCompleteAmount = titles.filter((t: any) => t.docStatus === 'OK').reduce((s: number, t: any) => s + t.amount, 0);
        const docsIncompleteAmount = totalAmount - docsCompleteAmount;
        return { totalAmount, validatedAmount, docsCompleteAmount, docsIncompleteAmount };
    }, [data, titles]);

    // Batch selection
    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (isLoading) return <PageSkeleton />;
    if (!data) return <div className="p-8 text-red-600">Garantia não encontrada</div>;

    return (
        <PageForm
            pageTitle={`${data.operationCode} — ${data.assignor}`}
            apiVersion={data.apiVersion ? `${data.apiName} - ${data.apiVersion} (${(data.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.guarantees,
                { label: data.operationCode, url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    {tabIndex === 2 && selectedIds.size > 0 && (
                        <button onClick={() => setShowValidationPanel(true)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm flex items-center space-x-2">
                            <FiCheck size={14} /> <span>Validar ({selectedIds.size})</span>
                        </button>
                    )}
                    <button onClick={() => navigate(COLLATERAL_ROUTES.guarantees)}
                        className="text-black bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded flex items-center">
                        <IoMdArrowRoundBack size={16} />
                    </button>
                    <button onClick={() => navigate(`/collateral/guarantees/${id}/view/edit`)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm flex items-center space-x-2">
                        <FiEdit2 size={14} /> <span>Editar</span>
                    </button>
                </div>
            }
        >
            {/* ===== TABS ===== */}
            <div className="mt-4">
                <Tab tabItems={TAB_ITEMS} tabIndex={tabIndex} setTabIndex={setTabIndex} />

                {/* Tab: Geral */}
                {tabIndex === 0 && (
                    <div className="mt-4 space-y-4">
                        {/* Cards superiores: Configuração | Resumo + Status */}
                        <div className="grid grid-cols-[2fr_1fr] gap-4">
                            {/* Card: Configuração */}
                            <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                                <SectionTitle color="text-green-700 dark:text-green-400">Configuração</SectionTitle>
                                <div className="grid grid-cols-2 gap-x-6 divide-x divide-green-200 dark:divide-green-800">
                                    <div className="space-y-0.5">
                                        <Field label="Tipo - Obrigação" value={`${data.operationType} - ${({'WITH_RECOURSE':'Com regresso','WITHOUT_RECOURSE':'Sem regresso','NO_OBLIGATION':'Sem garantia'} as Record<string,string>)[data.obligation] || data.obligation || '—'}`} />
                                        <Field label="Cedente" value={`${formatCpfCnpj(data.assignorTaxId)} - ${data.assignor}`} />
                                        <Field label="Risco" value={`${formatCurrency(data.assignorRisk || 0)} (${data.assignorRiskPercentage || 0}%)`} />
                                        <Field label="Principais sacados" value={data.mainDebtors} />
                                        <Field label="Intensidade" value={t(data.analysisIntensity)} color={data.analysisIntensity ? getIntensityColor(data.analysisIntensity) : undefined} />
                                        <Field label="Critério análise" value={data.analysisCriteria || '—'} />
                                        {data.sampledTitles && <Field label="Títulos amostragem" value={data.sampledTitles} />}
                                        <Field label="Data emissão" value={formatDate(data.issueDate)} />
                                    </div>
                                    <div className="space-y-0.5 pl-6">
                                        <Field label="Credora" value={data.creditor} />
                                        <Field label="Resp. análise" value={data.analystName} />
                                        <Field label="Trader" value={data.trader} />
                                        <Field label="Validação" value={data.validationAnalystName} />
                                        <Field label="Pendência" value={data.pendency} />
                                        <Field label="Observação" value={data.observation} />
                                        <Field label="Data com trader" value={formatDate(data.traderHandoverDate)} />
                                        <Field label="Follow up" value={data.followUpAreas} />
                                    </div>
                                </div>
                            </div>

                            {/* Right: Resumo + Status */}
                            <div className="flex flex-col gap-4">
                                <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                    <SectionTitle color="text-blue-700 dark:text-blue-400">Resumo</SectionTitle>
                                    <div className="space-y-1">
                                        <Field label="Nº de títulos" value={data.totalTitles} />
                                        <Field label="Moeda" value={data.currency || 'BRL'} />
                                        <Field label="Total de títulos" value={formatCurrency(summary.totalAmount)} />
                                        <Field label="Total validado" value={formatCurrency(summary.validatedAmount)} />
                                    </div>
                                </div>
                                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                                    <SectionTitle color="text-amber-700 dark:text-amber-400">Status</SectionTitle>
                                    <div className="space-y-1">
                                        <Field label="Status garantia" value={t(data.guaranteeStatus)} color={getGuaranteeStatusColor(data.guaranteeStatus)} />
                                        <Field label="Status trader" value={t(data.statusTrader)} color={getTraderStatusColor(data.statusTrader)} />
                                        <Field label="Status validação" value={t(data.validationStatus)} color={getValidationStatusColor(data.validationStatus)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Cards inferiores: Roxo + Rosa */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                                <Field label="Lastro salvo em garantias?" value={data.collateralSaved || 'N/A'} />
                            </div>
                            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded">
                                <Field label="Notificação sacados" value={data.debtorNotification || 'n/a'} />
                            </div>
                            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded space-y-2">
                                <Field label="Registro em cartório" value={data.courtRegistration ? 'SIM' : 'NÃO'} />
                                <Field label="Qual contrato registrar?" value={data.contractToRegister} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Métricas e checagem */}
                {tabIndex === 1 && (
                    <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-4 gap-3">
                            {ALL_PROCESSES.slice(0, 4).map(p => {
                                const isEnabled = enabledProcesses.includes(p);
                                const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                                return (
                                    <div key={p} className={`bg-white dark:bg-slate-800 border rounded p-3 ${isEnabled ? 'border-gray-200 dark:border-slate-700' : 'border-gray-100 dark:border-slate-800 opacity-50'}`}>
                                        <span className={`text-[10px] font-semibold uppercase ${isEnabled ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}`}>{PROCESS_LABELS[p]}</span>
                                        <div className="mt-2 space-y-1 text-xs">
                                            <div className="flex justify-between"><span className="text-gray-500">Obrigatório</span><span>{isEnabled ? 'Sim' : 'Não'}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Status</span><span>{d.status || '—'}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Recebidos</span><span>{d.received_count ?? d.receivedCount ?? 0}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Pendentes</span><span>{d.pending_count ?? d.pendingCount ?? 0}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Arquivados</span><span>{d.archived_count ?? d.archivedCount ?? 0}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Validados</span><span>{d.validated_count ?? d.validatedCount ?? 0}</span></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            {ALL_PROCESSES.slice(4).map(p => {
                                const isEnabled = enabledProcesses.includes(p);
                                const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                                return (
                                    <div key={p} className={`bg-white dark:bg-slate-800 border rounded p-3 ${isEnabled ? 'border-gray-200 dark:border-slate-700' : 'border-gray-100 dark:border-slate-800 opacity-50'}`}>
                                        <span className={`text-[10px] font-semibold uppercase ${isEnabled ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}`}>{PROCESS_LABELS[p]}</span>
                                        <div className="mt-2 space-y-1 text-xs">
                                            <div className="flex justify-between"><span className="text-gray-500">Obrigatório</span><span>{isEnabled ? 'Sim' : 'Não'}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Status</span><span>{d.status || '—'}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Recebidos</span><span>{d.received_count ?? d.receivedCount ?? 0}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Pendentes</span><span>{d.pending_count ?? d.pendingCount ?? 0}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Arquivados</span><span>{d.archived_count ?? d.archivedCount ?? 0}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Validados</span><span>{d.validated_count ?? d.validatedCount ?? 0}</span></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">Progresso do Lote</span>
                            <div className="mt-3 space-y-3">
                                {ALL_PROCESSES.filter(p => enabledProcesses.includes(p)).map(p => {
                                    const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                                    const total = d.total_count ?? d.totalCount ?? data.totalTitles ?? 0;
                                    const validated = d.validated_count ?? d.validatedCount ?? 0;
                                    const pct = total > 0 ? Math.round((validated / total) * 100) : 0;
                                    return (
                                        <div key={p}>
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="text-gray-600 dark:text-gray-300">{PROCESS_LABELS[p]}</span>
                                                <span className="text-gray-500">{validated}/{total} ({pct}%)</span>
                                            </div>
                                            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                                <div className={`h-2 rounded-full ${pct >= 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : pct > 0 ? 'bg-amber-500' : 'bg-gray-300'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Totals */}
                            <div className="border-t border-gray-200 dark:border-slate-700 mt-4 pt-3 space-y-2">
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-600 dark:text-gray-300 font-medium">Total de títulos</span>
                                    <span className="dark:text-white font-medium">{data.totalTitles}</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-600 dark:text-gray-300 font-medium">Valor total</span>
                                    <span className="dark:text-white font-medium">{formatCurrency(data.totalAmount || 0)}</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-600 dark:text-gray-300 font-medium">% validado</span>
                                    <span className="dark:text-white font-medium">{data.validatedPercentage || 0}%</span>
                                </div>
                            </div>

                            {/* Intensity */}
                            <div className="border-t border-gray-200 dark:border-slate-700 mt-3 pt-3">
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-gray-600 dark:text-gray-300 font-medium">Intensidade</span>
                                    {data.analysisIntensity && (
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getIntensityColor(data.analysisIntensity)}`}>{t(data.analysisIntensity)}</span>
                                    )}
                                </div>
                                <div className="flex justify-between text-xs mt-1">
                                    <span className="text-gray-500">Critério</span>
                                    <span className="dark:text-white">{data.analysisCriteria || '—'}</span>
                                </div>
                                {data.sampledTitles && (
                                    <div className="flex justify-between text-xs mt-1">
                                        <span className="text-gray-500">Títulos amostragem</span>
                                        <span className="dark:text-white">{data.sampledTitles}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Recebíveis */}
                {tabIndex === 2 && (
                    <div className="mt-3 overflow-hidden" style={{ height: 'calc(100vh - 160px)' }}>
                        <div className="h-full overflow-auto border border-gray-200 dark:border-slate-700">
                            <ATable
                                headers={[
                                    { id: 'titleNumber', title: 'Título', field: 'titleNumber', canSort: true, width: '60px' },
                                    { id: 'debtorName', title: 'Sacado', field: 'debtorName', canSort: true, width: '220px' },
                                    { id: 'city', title: 'Município - UF', field: 'city', canSort: true, width: '140px' },
                                    { id: 'receivableNumber', title: 'nDup', field: 'receivableNumber', canSort: true, width: '45px' },
                                    { id: 'amount', title: 'vDup', field: 'amount', canSort: true, width: '90px', cssClass: 'text-right' },
                                    { id: 'dueDate', title: 'Venc.', field: 'dueDate', canSort: true, width: '80px' },
                                    ...ALL_PROCESSES.map(p => ({
                                        id: `proc_${p}`,
                                        title: PROCESS_LABELS[p] || p,
                                        field: `proc_${p}`,
                                        canSort: false,
                                        width: '55px',
                                        cssClass: `text-center ${!enabledProcesses.includes(p) ? 'text-gray-300 dark:text-gray-600' : ''}`,
                                    })),
                                    { id: 'docStatus', title: 'Status', field: 'docStatus', canSort: true, width: '60px', cssClass: 'text-center' },
                                ]}
                                canSelectAll
                                onSelect={(checked: boolean) => {
                                    setSelectedIds(checked ? new Set(titles.map((t: any) => t.id)) : new Set());
                                }}
                            >
                                {titles.map((row: any, index: number) => {
                                    const processItems: Record<string, any> = {};
                                    (row.processItems || []).forEach((pi: any) => { processItems[pi.processType] = pi; });

                                    return (
                                        <tr key={row.id} className={`group hover:bg-blue-50 dark:hover:bg-slate-700 ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                                            <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center">
                                                <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)}
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500" />
                                            </td>
                                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.titleNumber}</td>
                                            <td className="py-1 px-3 border-b border-gray-200 dark:border-slate-700">
                                                <div className="text-xs dark:text-white truncate" title={row.debtorName}>{row.debtorName}</div>
                                                <div className="text-[10px] text-gray-400 truncate" title={row.nfeKey}>{row.debtorDocument} · {row.nfeKey}</div>
                                            </td>
                                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.city}{row.state ? ` - ${row.state}` : ''}</td>
                                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.receivableNumber || '—'}</td>
                                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white text-right">{formatCurrency(row.amount)}</td>
                                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{formatDate(row.dueDate)}</td>
                                            {ALL_PROCESSES.map(p => {
                                                const pi = processItems[p];
                                                const isEnabled = enabledProcesses.includes(p);
                                                return (
                                                    <td key={p} className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center">
                                                        {isEnabled ? (
                                                            <div className="flex flex-col items-center gap-0.5">
                                                                {pi?.received
                                                                    ? <MdOutlineCheckCircle className="text-green-400" size={10} title="Recebido" />
                                                                    : <MdOutlineRadioButtonUnchecked className="text-gray-300" size={10} title="Não recebido" />
                                                                }
                                                                <ProcessIcon received={pi?.received} status={pi?.status} />
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-200 dark:text-gray-700">—</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs text-center">
                                                <span className={row.docStatus === 'OK' ? 'text-green-600 font-medium' : 'text-gray-400'}>{row.docStatus || ''}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </ATable>
                        </div>
                    </div>
                )}
            </div>

            {/* Validation side panel */}
            {showValidationPanel && (
                <div className="fixed inset-0 z-50 flex">
                    <div className="flex-1 bg-black/30" onClick={() => setShowValidationPanel(false)} />
                    <div className="w-[600px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto border-l border-gray-200 dark:border-slate-700">
                        <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10">
                            <span className="text-sm font-semibold dark:text-white">Validar {selectedIds.size} recebível(is)</span>
                            <div className="flex items-center space-x-2">
                                <button className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs flex items-center space-x-1">
                                    <FiCheck size={12} /> <span>Salvar</span>
                                </button>
                                <button onClick={() => setShowValidationPanel(false)} className="text-gray-400 hover:text-gray-600"><IoMdClose size={18} /></button>
                            </div>
                        </div>
                        <div className="p-4 space-y-4">
                            {/* Selected receivables summary */}
                            <div className="bg-gray-50 dark:bg-slate-900 rounded p-3 border border-gray-100 dark:border-slate-700">
                                <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Recebíveis selecionados</div>
                                <div className="max-h-32 overflow-y-auto space-y-1">
                                    {titles.filter((t: any) => selectedIds.has(t.id)).map((t: any) => (
                                        <div key={t.id} className="flex justify-between text-xs">
                                            <span className="dark:text-white">{t.titleNumber} - {t.debtorName}</span>
                                            <span className="text-gray-500">{formatCurrency(t.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Validation fields per enabled process */}
                            <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Validações por processo</div>
                            <div className="grid grid-cols-2 gap-3">
                                {ALL_PROCESSES.filter(p => enabledProcesses.includes(p) && p !== 'DRAWEE').map(p => (
                                    <div key={p} className="border border-gray-200 dark:border-slate-700 rounded p-3 space-y-2">
                                        <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">{PROCESS_LABELS[p]}</span>
                                        <SelectTextField label="Recebido" value="" items={[{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]} onChange={() => {}} />
                                        <SelectTextField label="Status" value="" items={[{ value: 'PENDING', label: 'Pendente' }, { value: 'VALIDATED', label: 'Validado' }, { value: 'REJECTED', label: 'Rejeitado' }, { value: 'ARCHIVED', label: 'Arquivado' }]} onChange={() => {}} />
                                        <TextEdit label="Observação" value="" onChange={() => {}} />
                                    </div>
                                ))}
                            </div>

                            {/* Batch status + observation */}
                            <div className="border-t border-gray-200 dark:border-slate-700 pt-4 space-y-3">
                                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Status geral do recebível</div>
                                <SelectTextField label="Status" value="" items={[{ value: 'PENDING', label: 'Pendente' }, { value: 'VALIDATED', label: 'Validado' }, { value: 'REJECTED', label: 'Rejeitado' }, { value: 'ARCHIVED', label: 'Arquivado' }]} onChange={() => {}} />
                                <TextEdit label="Observação" value="" onChange={() => {}} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageForm>
    );
};

export default GuaranteePageView;
